import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react'
import { useSessionStateStore } from '@renderer/stores/sessionState'
import { useConfigStore } from '@renderer/stores/configState'
import { usePrintStore } from '@renderer/stores/printStore'
import { useTranslation } from 'react-i18next'
import { initiatePrint } from '@renderer/utils/pipeline/printService'
import cogoToast from '@successtar/cogo-toast'
import { BackgroundContainer, GlassPanel } from '@renderer/components/BackgroundContainer'
import { Button, QrCodeImage } from '@renderer/components'
import { FaMinus, FaPlus } from 'react-icons/fa'
import MidtransUtils, { VoucherOnlyRegionError } from '@renderer/utils/midtransUtils'
import { useInterval } from '@renderer/utils/useInterval'
import { useOutletContext } from 'react-router-dom'

import PinModal from '@renderer/components/PinModal'
import OnScreenKeyboard from '@renderer/components/OnScreenKeyboard'
import { useSettingsStore } from '@renderer/stores/settingsStore'
import { getInternetStatus, onInternetStatusChanged } from '@renderer/lib/runtime'
import {
  closeToyyibpayBill,
  hasToyyibpayBillApi,
  openToyyibpayBill
} from '@renderer/lib/runtime/bridge'
import { useDesignLayout } from '@renderer/hooks'
import { formatAmount, normalizeCurrency } from '@renderer/utils/formatCurrency'
import { qrActivityLogger } from '@renderer/utils/qrLoadReporter'

interface UserReprintModalProps {
  isVisible: boolean
  onComplete: () => void
  onSkip: () => void
  stripPreview?: string
  /** When true, stripPreview is a video URL (hero preview); printing still uses the strip image from gallery. */
  stripPreviewIsVideo?: boolean
  timeout?: number // Optional timeout in seconds, defaults to 60
}

const UserReprintModal: React.FC<UserReprintModalProps> = ({
  isVisible,
  onComplete,
  onSkip,
  stripPreview: propStripPreview,
  stripPreviewIsVideo = false,
  timeout = 60
}) => {
  const { t } = useTranslation()
  const { isCompact: rpCompact, isTiny: rpTiny, isLandscape, isSpacious } = useDesignLayout()
  const rpModalRowLayout = isLandscape
  const rpLandscapeShort = rpCompact && isLandscape
  const rpContentJustify = rpModalRowLayout ? 'justify-center' : 'justify-start'

  // --- Consistent container sizing (matches ConsentModal) ---
  const outerPad = rpCompact ? 'p-2' : isSpacious ? 'p-4' : 'p-6'
  const glassPanelClass = rpCompact
    ? 'h-auto w-full max-w-7xl max-h-[96dvh] overflow-y-auto'
    : rpModalRowLayout
      ? `h-auto w-full max-h-[92dvh] overflow-y-auto ${isSpacious ? 'max-w-[min(94vw,1400px)]' : 'max-w-4xl'}`
      : `h-auto w-full max-h-[94dvh] overflow-y-auto ${isSpacious ? 'max-w-[min(92vw,900px)]' : 'max-w-[min(90vw,640px)]'}`

  const stripPaneClass = rpModalRowLayout
    ? rpLandscapeShort
      ? 'min-h-0 min-w-0 max-h-full w-[38%] max-w-[40%] shrink-0 p-2'
      : rpCompact
        ? 'min-h-0 min-w-0 max-h-full w-[38%] max-w-[40%] shrink-0 p-2'
        : isSpacious
          ? 'min-h-0 min-w-0 max-h-full w-[40%] max-w-[42%] shrink-0 p-6 sm:p-8'
          : 'min-h-0 min-w-0 max-h-full w-[340px] shrink-0 p-6'
    : rpCompact
      ? 'w-full shrink-0 p-2'
      : isSpacious
        ? 'w-full shrink-0 p-6 sm:p-8'
        : 'max-h-[min(40dvh,440px)] min-h-[10rem] w-full shrink-0 p-6'

  const stripMediaMax = rpModalRowLayout
    ? 'max-h-full max-w-full'
    : rpCompact
      ? 'max-h-[min(36dvh,300px)] max-w-full'
      : isSpacious
        ? 'max-h-[min(62dvh,820px)] w-auto max-w-[min(88vw,560px)]'
        : 'max-h-[min(40dvh,440px)] max-w-full'

  const contentPadClass = rpLandscapeShort
    ? 'px-3 py-2'
    : rpModalRowLayout && rpCompact
      ? 'px-4 py-3'
      : rpModalRowLayout
        ? isSpacious ? 'p-10 lg:p-12' : 'p-10'
        : rpCompact
          ? 'px-4 py-3'
          : isSpacious ? 'px-10 py-8 sm:px-12 sm:py-10' : 'px-8 py-6 sm:p-10'

  // --- Responsive typography ---
  const reprintTitleClass = rpTiny
    ? 'mb-1 text-lg font-bold text-gray-800'
    : rpCompact
      ? 'mb-2 text-xl font-bold text-gray-800'
      : isSpacious
        ? 'mb-4 text-5xl font-bold text-gray-800'
        : 'mb-4 text-4xl font-bold text-gray-800'
  const reprintSubtitleClass = rpTiny
    ? 'text-xs leading-snug text-gray-600'
    : rpCompact
      ? 'text-sm leading-relaxed text-gray-600'
      : isSpacious
        ? 'text-2xl leading-relaxed text-gray-600'
        : 'text-xl leading-relaxed text-gray-600'
  const sectionGap = rpTiny ? 'mb-3' : rpCompact ? 'mb-4' : isSpacious ? 'mb-10' : 'mb-8'
  const stepperOuterGap = rpTiny ? 'gap-3' : rpCompact ? 'gap-4' : isSpacious ? 'gap-10' : 'gap-8'
  const stepperBtnClass =
    'flex shrink-0 items-center justify-center rounded-full bg-white text-gray-700 shadow-lg transition-all hover:bg-gray-50 hover:shadow-xl active:scale-95 disabled:cursor-not-allowed disabled:opacity-50 ' +
    (rpTiny ? 'h-9 w-9' : rpCompact ? 'h-11 w-11' : isSpacious ? 'h-20 w-20' : 'h-16 w-16')
  const stepperIconClass = rpTiny ? 'text-xs' : rpCompact ? 'text-base' : isSpacious ? 'text-2xl' : 'text-xl'
  const qtyNumClass = rpTiny ? 'text-2xl' : rpCompact ? 'text-3xl' : isSpacious ? 'text-7xl' : 'text-6xl'
  const qtyColMin = rpTiny ? 'min-w-[4.5rem]' : rpCompact ? 'min-w-[5rem]' : isSpacious ? 'min-w-[9rem]' : 'min-w-[7.5rem]'
  const qtyLabelClass = rpTiny
    ? 'mt-0.5 text-[10px] font-semibold uppercase tracking-widest text-gray-500'
    : rpCompact
      ? 'mt-1 text-xs font-semibold uppercase tracking-widest text-gray-500'
      : isSpacious
        ? 'mt-1.5 text-base font-semibold uppercase tracking-widest text-gray-500'
        : 'mt-1 text-sm font-semibold uppercase tracking-widest text-gray-500'
  const reprintPrimaryBtn =
    'w-full !inline-flex !items-center !justify-center font-semibold shadow-lg transition-shadow hover:shadow-xl ' +
    (rpTiny
      ? '!px-4 !py-2 !text-sm !leading-snug'
      : rpCompact
        ? '!px-5 !py-2.5 !text-base !leading-snug'
        : isSpacious
          ? '!px-8 !py-4 !text-xl !leading-snug'
          : '!px-6 !py-3 !text-lg !leading-snug')
  const reprintSecondaryBtn =
    'w-full !inline-flex !items-center !justify-center font-semibold ' +
    (rpTiny
      ? '!px-4 !py-2 !text-sm !leading-snug'
      : rpCompact
        ? '!px-5 !py-2.5 !text-base !leading-snug'
        : isSpacious
          ? '!px-8 !py-4 !text-xl !leading-snug'
          : '!px-6 !py-3 !text-lg !leading-snug')
  const buttonGap = rpTiny ? 'gap-1.5' : rpCompact ? 'gap-2' : isSpacious ? 'gap-5' : 'gap-4'
  const priceMainClass = rpTiny ? 'text-lg font-bold' : rpCompact ? 'text-xl font-bold' : isSpacious ? 'text-3xl font-bold' : 'text-2xl font-bold'
  const paymentTitleClass = rpTiny ? 'mb-2 text-lg font-bold' : rpCompact ? 'mb-3 text-xl font-bold' : isSpacious ? 'mb-6 text-4xl font-bold' : 'mb-6 text-3xl font-bold'
  const paymentQrBoxClass = rpTiny
    ? 'mb-2 flex h-[140px] w-[140px] flex-col items-center justify-center rounded-2xl bg-white p-3 shadow-lg'
    : rpCompact
      ? 'mb-3 flex h-[180px] w-[180px] flex-col items-center justify-center rounded-2xl bg-white p-4 shadow-lg'
      : isSpacious
        ? 'mb-6 flex h-[340px] w-[340px] flex-col items-center justify-center rounded-2xl bg-white p-8 shadow-lg'
        : 'mb-6 flex h-[300px] w-[300px] flex-col items-center justify-center rounded-2xl bg-white p-8 shadow-lg'
  const paymentPriceClass = rpTiny ? 'text-xl font-bold' : rpCompact ? 'text-2xl font-bold' : isSpacious ? 'text-4xl font-bold' : 'text-3xl font-bold'
  const countdownClass = rpTiny ? 'text-xs' : rpCompact ? 'text-sm' : isSpacious ? 'text-xl' : 'text-lg'
  const countdownPos = rpTiny ? 'right-2 top-2' : rpCompact ? 'right-3 top-3' : isSpacious ? 'right-8 top-8' : 'right-6 top-6'

  const { gallery, id, userPreferences, logActivity, setSessionState, uploadSessionState } = useSessionStateStore()
  const { config: globalConfig, frames } = useConfigStore()
  const { isPrintingEnabled, getPrinterForFrame } = usePrintStore()
  const { password } = useSettingsStore()
  
  // Outlet context retained for forward-compat; reprint pricing now uses
  // the project's stamped resolvedCurrency rather than email-based detection.
  useOutletContext<{ user: { email: string } } | undefined>()

  const projectCurrency = normalizeCurrency(globalConfig?.resolvedCurrency)

  // Voucher-only customer flow (mirrors view/Checkout + view/Payment):
  // when the project's stamped payment tier has no QRIS rail and the
  // operator hasn't wired a custom gateway, the customer must enter a
  // voucher to reprint. The operator-PIN bypass below stays for the
  // separate offline / control-panel override path.
  const reprintTier = globalConfig?.paymentSetupTier
  const reprintHasCustomGateway = Boolean(globalConfig?.hasCustomGateway)
  // `global_hidden` (e.g. MXN) is voucher-only ONLY without a custom gateway;
  // a wired Mercado Pago makes online payment available. See view/Payment.tsx.
  const isVoucherOnlyRegion =
    (reprintTier === 'global_hidden' ||
      reprintTier === 'localized_in_progress') &&
    !reprintHasCustomGateway

  const stripUrlForPrint = useMemo(() => {
    const stripItem = gallery.capturedItems.find((item) => item.id?.includes('dreambooth-strip'))
    return stripItem?.stripUrl || gallery.stripUrl || ''
  }, [gallery.capturedItems, gallery.stripUrl])

  const [stripPreview, setStripPreview] = useState<string>(propStripPreview || '')
  const [isProcessing, setIsProcessing] = useState<boolean>(false)

  const [showPayment, setShowPayment] = useState<boolean>(false)
  const [showPinModal, setShowPinModal] = useState<boolean>(false)
  const [showVoucherInput, setShowVoucherInput] = useState<boolean>(false)
  const [voucherError, setVoucherError] = useState<string>('')
  const [isVoucherLoading, setIsVoucherLoading] = useState<boolean>(false)
  const [qrUrl, setQrUrl] = useState<string>('')
  const [snapToken, setSnapToken] = useState<string>('')
  // Redirect rail (e.g. ToyyibPay): the gateway bill opens in a frameless
  // kiosk BrowserWindow via the bridge; mirrors the Payment page flow.
  const [redirectUrl, setRedirectUrl] = useState<string>('')
  const [billWindowOpen, setBillWindowOpen] = useState<boolean>(false)
  const [billWindowError, setBillWindowError] = useState<string | null>(null)
  const billWindowOpenedForUrlRef = useRef<string | null>(null)
  const [isCheckingPayment, setIsCheckingPayment] = useState<boolean>(false)
  const [isExpired, setIsExpired] = useState<boolean>(false)
  const [isOnline, setIsOnline] = useState<boolean>(true)
  const [orderId, setOrderId] = useState<string | null>(null)
  const [remainingTime, setRemainingTime] = useState<number>(timeout)

  const isMountedRef = useRef<boolean>(true)
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)
  const countdownRef = useRef<NodeJS.Timeout | null>(null)
  const paymentHandledRef = useRef<boolean>(false)
  const isPrintingRef = useRef<boolean>(false)

  const packages = useMemo(
    () => globalConfig?.pages?.result?.settings?.reprintPackages || globalConfig?.pages?.checkout?.settings?.packages || [],
    [globalConfig]
  )
  const printPackages = useMemo(
    () =>
      packages
        .filter((pkg) => pkg.type === 'print')
        .sort((a, b) => (a.quantity || 0) - (b.quantity || 0)),
    [packages]
  )

  const [selectedQuantity, setSelectedQuantity] = useState<number>(() => {
    return printPackages.length > 0 ? printPackages[0].quantity : 1
  })

  const selectedPackage = useMemo(() => {
    return printPackages.find((pkg) => pkg.quantity === selectedQuantity)
  }, [printPackages, selectedQuantity])

  const packagePrice = selectedPackage?.price || 0

  /**
   * Editor-driven per-frame add-on for THIS session's frame. Added ONCE per
   * reprint order (not × copies), deliberately divergent from the bahela
   * precedent which does not apply the surcharge to reprint (spec D3).
   * Resolve from the session preference first (written on the Frame page);
   * fall back to looking up the strip's frame in the frames catalog so a
   * reprint after a fresh app launch (empty userPreferences) still charges it.
   */
  const frameAddon = useMemo(() => {
    const fromPref = userPreferences.frameAddonPricePerPackage
    if (typeof fromPref === 'number' && fromPref > 0) return fromPref
    const manifestFrameId = gallery.compositionManifest?.frameId
    if (manifestFrameId) {
      const catalogFrame = frames.find((f) => f._id === manifestFrameId)
      if (catalogFrame?.addOnPrice && catalogFrame.addOnPrice > 0) {
        return catalogFrame.addOnPrice
      }
    }
    return 0
  }, [userPreferences.frameAddonPricePerPackage, gallery.compositionManifest?.frameId, frames])

  /**
   * Total the customer sees and pays for a reprint order: the print package
   * price plus the frame add-on once. When the base package is free the add-on
   * is still not charged (no printed strip -> nothing to surcharge), matching
   * checkout, which only adds it alongside a selected print package.
   */
  const currentPrice = packagePrice > 0 ? packagePrice + frameAddon : packagePrice

  const paymentTimeoutConfig = globalConfig?.pages?.payment?.settings?.paymentTimeout || 120

  useEffect(() => {
    if (!isVisible) return

    isMountedRef.current = true

    setRemainingTime(timeout)
    setShowPayment(false)
    setIsProcessing(false)

    if (propStripPreview) {
      setStripPreview(propStripPreview)
    } else {
      const stripItem = gallery.capturedItems.find((item) => item.id?.includes('dreambooth-strip'))
      const previewUrl = stripItem?.stripUrl || gallery.stripUrl || ''
      setStripPreview(previewUrl)
    }

    countdownRef.current = setInterval(() => {
      setRemainingTime((prev) => {
        if (prev <= 1) {
          if (isMountedRef.current && !showPayment) {
            logActivity('Reprint modal timed out, skipping')
            onSkip()
          }
          return 0
        }
        return prev - 1
      })
    }, 1000)

    void getInternetStatus().then((status) => setIsOnline(status))
    const unsubscribeNet = onInternetStatusChanged(setIsOnline)

    return (): void => {
      isMountedRef.current = false
      if (countdownRef.current) clearInterval(countdownRef.current)
      if (timeoutRef.current) clearTimeout(timeoutRef.current)
      unsubscribeNet()
      // Best-effort close of the redirect popup when the modal unmounts.
      closeToyyibpayBill()
    }
  }, [isVisible, timeout, propStripPreview, gallery])

  useEffect(() => {
    if (showPayment && countdownRef.current) {
      clearInterval(countdownRef.current)
      countdownRef.current = null
    }
  }, [showPayment])

  const handleQuantityChange = (direction: 'increase' | 'decrease'): void => {
    if (printPackages.length === 0) return

    const currentIndex = printPackages.findIndex((pkg) => pkg.quantity === selectedQuantity)
    if (currentIndex === -1) return

    let nextIndex = currentIndex
    if (direction === 'increase') {
      nextIndex = Math.min(printPackages.length - 1, currentIndex + 1)
    } else {
      nextIndex = Math.max(0, currentIndex - 1)
    }

    setSelectedQuantity(printPackages[nextIndex].quantity)
  }

  const handleSkip = (): void => {
    logActivity('User skipped reprint')
    onSkip()
  }

  const handlePayment = async (): Promise<void> => {
    if (!selectedPackage) {
      cogoToast.error('Invalid selection')
      return
    }

    // Charge the package price plus the per-frame add-on (once per order).
    const grossAmount = Math.max(0, currentPrice)
    if (grossAmount <= 0) {
      logActivity('User requested free reprint', { quantity: selectedQuantity })
      setIsProcessing(true)
      await handlePrint()
      return
    }

    // Voucher-only region: never call /api/payments. Open the customer
    // voucher input — cash vouchers settle immediately and proceed to
    // printing; discount vouchers reduce the price but still require
    // operator-handled offline payment.
    if (isVoucherOnlyRegion) {
      logActivity('Reprint redirected to voucher entry (voucher-only region)', {
        paymentSetupTier: reprintTier
      })
      setVoucherError('')
      setShowVoucherInput(true)
      return
    }

    if (!isOnline) {
      cogoToast.error('No internet connection')
      return
    }

    setIsProcessing(true)
    setShowPayment(true)
    setIsExpired(false)
    setQrUrl('')
    setSnapToken('')
    paymentHandledRef.current = false
    isPrintingRef.current = false

    try {
      console.log('Initiating payment for amount:', currentPrice)
      const data = await MidtransUtils.initiatePayment(
        currentPrice,
        id || '',
        paymentTimeoutConfig,
        globalConfig?._id || '',
        userPreferences.filterId || '',
        userPreferences.frameId || '',
        0,
        true
      )

      console.log('Payment initiation response:', data)

      if (data.order_id) {
        setOrderId(data.order_id)

        if (data.qr_url) {
          const sanitizedQrUrl = data.qr_url.replace(/([^:]\/)\/.+/g, '$1')
          setQrUrl(sanitizedQrUrl)
          setIsCheckingPayment(true)
          console.log(`[REPRINT] Payment initiated with QR, isCheckingPayment set to true`)
          logActivity('Reprint Payment Initiated (QR)', {
            orderId: data.order_id,
            amount: currentPrice,
            type: data.payment_type
          })
        } else if (data.snap_token) {
          setSnapToken(data.snap_token)
          console.log(`[REPRINT] Payment initiated with Snap, isCheckingPayment set to true`)
          logActivity('Reprint Payment Initiated (Snap)', {
            orderId: data.order_id,
            snapToken: data.snap_token
          })
          setIsCheckingPayment(true)
        } else if (data.redirect_url) {
          // Redirect rail (ToyyibPay): poll for settlement while the frameless
          // bill window (auto-opened below) collects payment.
          setRedirectUrl(data.redirect_url)
          setIsCheckingPayment(true)
          logActivity('Reprint Payment Initiated (Redirect)', {
            orderId: data.order_id,
            provider: data.provider,
            type: data.payment_type
          })
        } else {
          console.error('No QR URL, Snap Token, or redirect URL found in response:', data)
          throw new Error(`Invalid Payment Response: Type=${data.payment_type}`)
        }
      } else {
        throw new Error('Failed to generate payment order')
      }
    } catch (error) {
      // Backend declined QRIS for this region — switch to the customer
      // voucher input, same contract as view/Payment.tsx.
      if (error instanceof VoucherOnlyRegionError) {
        logActivity('Reprint switched to voucher entry (backend 409)', {
          paymentSetupTier: error.paymentSetupTier
        })
        setShowPayment(false)
        setIsProcessing(false)
        setVoucherError('')
        setShowVoucherInput(true)
        return
      }
      console.error('Payment initiation error:', error)
      const msg = error instanceof Error ? error.message : 'Unknown Error'
      cogoToast.error(`Payment Error: ${msg}`)
      setShowPayment(false)
      setIsProcessing(false)
    }
  }

  /**
   * Customer voucher path for reprint — NOT the operator-PIN bypass.
   * Validates the code via `/api/promos/apply` (same endpoint Checkout
   * uses). Cash vouchers settle and print immediately; discount vouchers
   * display the residual amount and ask the customer to settle offline
   * with the operator. Mirrors view/Checkout's `handlePromoEnter`.
   */
  const handleVoucherSubmit = async (code: string): Promise<void> => {
    if (!selectedPackage) {
      cogoToast.error('Invalid selection')
      return
    }
    const trimmed = (code || '').trim()
    if (!trimmed) {
      setVoucherError(t('checkout.invalidPromo', 'Invalid promo code'))
      return
    }
    setVoucherError('')
    setIsVoucherLoading(true)
    try {
      // Include the per-frame add-on in the reprint subtotal the voucher applies against.
      const subtotal = Math.max(0, currentPrice)
      const response = await fetch(
        `${process.env.VITE_API_URL}/api/promos/apply`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            code: trimmed,
            projectId: globalConfig?._id || '',
            amount: Math.round(subtotal)
          })
        }
      )
      const data = await response.json().catch(() => ({}))
      const isValid = response.ok && data.isValid
      const isCash = Boolean(isValid && data.isCash)

      if (!isValid) {
        const msg =
          data.message || data.error || t('checkout.invalidPromo', 'Invalid promo code')
        setVoucherError(msg)
        cogoToast.error(msg)
        return
      }

      if (isCash) {
        logActivity('Reprint cash voucher applied', {
          code: trimmed,
          quantity: selectedQuantity,
          amount: subtotal
        })
        cogoToast.success(t('checkout.promoApplied', 'Promo code applied successfully!'))
        setShowVoucherInput(false)
        setIsProcessing(true)
        await handlePrint()
        return
      }

      const apiDiscount = Math.round(Number(data.discount) || 0)
      const finalAmount = Math.max(0, Math.round(subtotal - apiDiscount))
      logActivity('Reprint discount voucher applied (operator settlement)', {
        code: trimmed,
        quantity: selectedQuantity,
        discount: apiDiscount,
        finalAmount
      })
      cogoToast.info(
        t(
          'reprint.voucherDiscountedSettleOffline',
          'Voucher applied. Please settle {{amount}} with the operator to continue.',
          { amount: formatAmount(finalAmount, projectCurrency) }
        )
      )
    } catch (error) {
      console.error('Voucher validation error:', error)
      const msg = t(
        'checkout.promoValidationFailed',
        'Failed to validate promo code. Please try again.'
      )
      setVoucherError(msg)
      cogoToast.error(msg)
    } finally {
      setIsVoucherLoading(false)
    }
  }

  const openSnapPayment = (): void => {
    if (!snapToken || !window.snap) {
      cogoToast.error('Payment gateway not ready')
      return
    }

    window.snap.pay(snapToken, {
      onSuccess: async (result: unknown) => {
        console.log('Snap Success:', result)
        logActivity('Snap Payment Success', { result })
        await handlePrint()
      },
      onPending: (result: unknown) => {
        console.log('Snap Pending:', result)
        setIsCheckingPayment(true)
      },
      onError: (result: unknown) => {
        console.error('Snap Error:', result)
        cogoToast.error('Payment failed')
      },
      onClose: () => {
        console.log('Snap Closed')
      }
    })
  }

  const handlePrint = async (): Promise<void> => {
    if (isPrintingRef.current) {
      console.log(`[REPRINT] handlePrint skipped - already printing`)
      return
    }
    isPrintingRef.current = true
    paymentHandledRef.current = true
    setIsCheckingPayment(false)
    console.log(`[REPRINT] handlePrint called`)
    
    const currentState = useSessionStateStore.getState()
    const currentExtraPrint = currentState.transaction.extraPrintAmount || 0
    const newExtraPrint = currentExtraPrint + currentPrice
    const currentExtraCopies = currentState.transaction.extraPrintCopies || 0
    const newExtraCopies = currentExtraCopies + selectedQuantity
    
    console.log(`[REPRINT] Updating extraPrintAmount: ${currentExtraPrint} + ${currentPrice} = ${newExtraPrint}`)
    console.log(`[REPRINT] Updating extraPrintCopies: ${currentExtraCopies} + ${selectedQuantity} = ${newExtraCopies}`)
    
    setSessionState({
      transaction: {
        extraPrintAmount: newExtraPrint,
        extraPrintCopies: newExtraCopies,
        extraPrintPaymentStatus: 'bypass'
      }
    })
    
    const updatedState = useSessionStateStore.getState()
    console.log(`[REPRINT] State after update - extraPrintAmount: ${updatedState.transaction.extraPrintAmount}, extraPrintCopies: ${updatedState.transaction.extraPrintCopies}`)
    console.log(`[REPRINT] Uploading session ${id} with reprint data...`)
    
    try {
      await uploadSessionState(id)
      console.log(`[REPRINT] Session ${id} uploaded successfully with extraPrintAmount: ${newExtraPrint}`)
    } catch (err) {
      console.error(`[REPRINT] Failed to upload session ${id} after reprint:`, err)
    }
    
    if (!isPrintingEnabled) {
      cogoToast.warn('Printing is disabled')
    } else {
      try {
        const printerConfig = getPrinterForFrame(userPreferences.frameId || '')
        if (!printerConfig) {
          throw new Error('No printer available')
        }

        cogoToast.loading('Printing your photos...')

        const printImageUrl = stripUrlForPrint || stripPreview
        const response = await fetch(printImageUrl)
        const blob = await response.blob()

        const reprintId = `reprint-${id || 'session'}-${Date.now()}`

        await initiatePrint(
          blob,
          printerConfig.printerName,
          selectedQuantity,
          printerConfig.settings,
          true,
          reprintId,
          'user_reprint'
        )

        cogoToast.success('Printing started!')
        logActivity(`User reprinted ${selectedQuantity} copies`)
      } catch (error) {
        console.error('Print error:', error)
        cogoToast.error('Failed to print')
      }
    }

    isPrintingRef.current = false
    setIsProcessing(false)
    setTimeout(() => {
      if (isMountedRef.current) {
        onComplete()
      }
    }, 2000)
  }

  // Open the redirect gateway bill in a frameless kiosk BrowserWindow via the
  // bridge (same flow as the Payment page). Only opens once per URL; status
  // polling settles the reprint regardless of when the window closes.
  const openBillWindow = useCallback(
    async (urlOverride?: string): Promise<void> => {
      const url = urlOverride || redirectUrl
      if (!url) return
      if (!hasToyyibpayBillApi()) {
        setBillWindowError(
          t('payment.popupUnavailable', 'Payment window is unavailable in this build.')
        )
        return
      }
      try {
        setBillWindowError(null)
        setBillWindowOpen(true)
        billWindowOpenedForUrlRef.current = url
        const result = await openToyyibpayBill(url)
        if (result.status === 'reused') return
        setBillWindowOpen(false)
        if (result.status === 'unavailable') {
          setBillWindowError(
            t('payment.popupUnavailable', 'Payment window is unavailable in this build.')
          )
          logActivity('Reprint bill window unavailable', { url, reason: result.reason })
        } else if (result.status === 'failed') {
          setBillWindowError(
            t('payment.popupFailed', 'Could not open the payment window. Please try again.')
          )
          logActivity('Reprint bill window failed', { url, reason: result.reason })
        } else {
          logActivity('Reprint bill window closed', { url, finalUrl: result.finalUrl })
        }
      } catch (error) {
        setBillWindowOpen(false)
        setBillWindowError(
          t('payment.popupFailed', 'Could not open the payment window. Please try again.')
        )
        logActivity('Reprint bill window error', {
          url,
          error: error instanceof Error ? error.message : 'unknown'
        })
      }
    },
    [redirectUrl, t]
  )

  const handleCancelPayment = (): void => {
    setShowPayment(false)
    setIsCheckingPayment(false)
    setOrderId(null)
    setIsProcessing(false)
    setSnapToken('')
    setQrUrl('')
    setRedirectUrl('')
    setBillWindowError(null)
    setBillWindowOpen(false)
    billWindowOpenedForUrlRef.current = null
    closeToyyibpayBill()

    setRemainingTime(timeout)
    countdownRef.current = setInterval(() => {
      setRemainingTime((prev) => {
        if (prev <= 1) {
          if (isMountedRef.current && !showPayment) {
            logActivity('Reprint modal timed out, skipping')
            onSkip()
          }
          return 0
        }
        return prev - 1
      })
    }, 1000)
  }

  const handlePinSubmit = async (pin: string): Promise<void> => {
    if (pin === password) {
      logActivity('Reprint payment bypassed with PIN', { quantity: selectedQuantity })
      setShowPinModal(false)
      if (showPayment) {
        handleCancelPayment()
      }
      setIsProcessing(true)
      await handlePrint()
    } else {
      cogoToast.error(t('payment.wrongCode', 'Incorrect code. Please try again.'))
    }
  }

  useEffect(() => {
    const handleBypass = async (): Promise<void> => {
      logActivity('Reprint payment bypassed via Control Panel')
      handleCancelPayment()
      await handlePrint()
    }

    window.addEventListener('user-reprint-bypass', handleBypass)
    return (): void => {
      window.removeEventListener('user-reprint-bypass', handleBypass)
    }
  }, [selectedQuantity, isPrintingEnabled])

  useEffect(() => {
    const event = new CustomEvent('reprint-payment-state', {
      detail: { visible: showPayment }
    })
    window.dispatchEvent(event)

    return (): void => {
      const cleanupEvent = new CustomEvent('reprint-payment-state', {
        detail: { visible: false }
      })
      window.dispatchEvent(cleanupEvent)
    }
  }, [showPayment])

  // Auto-open the redirect bill popup once a URL is available (once per URL).
  useEffect(() => {
    if (!showPayment) return
    if (!redirectUrl) return
    if (isExpired) return
    if (paymentHandledRef.current) return
    if (billWindowOpenedForUrlRef.current === redirectUrl) return
    void openBillWindow(redirectUrl)
  }, [showPayment, redirectUrl, isExpired, openBillWindow])

  // Close the popup once the payment expires so the user isn't left on a dead
  // bill page (settlement/cancel close it via their own handlers).
  useEffect(() => {
    if (isExpired) closeToyyibpayBill()
  }, [isExpired])

  useInterval(
    async () => {
      if (paymentHandledRef.current) return
      if (!orderId) return
      try {
        const paymentData = await MidtransUtils.checkPaymentStatus(orderId)

        if (paymentData.isExpired) {
          setIsExpired(true)
          setIsCheckingPayment(false)
          cogoToast.warn('Payment expired')
        } else if (paymentData.paid) {
          if (paymentHandledRef.current) return
          closeToyyibpayBill()
          cogoToast.success('Payment successful!')
          logActivity('Reprint Payment Successful (Verified)', { orderId })
          await handlePrint()
        }
      } catch (error) {
        console.error('Error checking payment status:', error)
      }
    },
    isCheckingPayment && isOnline ? 3000 : null
  )

  const isMinQuantity =
    printPackages.length > 0 && selectedQuantity === printPackages[0].quantity
  const isMaxQuantity =
    printPackages.length > 0 &&
    selectedQuantity === printPackages[printPackages.length - 1].quantity

  if (!isVisible) return null

  return (
    <BackgroundContainer className={`fixed inset-0 z-50 flex items-center justify-center ${outerPad}`}>
      <GlassPanel className={`relative z-10 mx-auto flex flex-col rounded-3xl ${glassPanelClass}`}>
        {/* Countdown Display */}
        {!showPayment && (
          <div className={`absolute z-10 ${countdownPos}`}>
            <span className={`font-mono font-semibold tabular-nums text-gray-700 ${countdownClass}`}>
              {Math.floor(remainingTime / 60)}:{String(remainingTime % 60).padStart(2, '0')}
            </span>
          </div>
        )}

        <div className={`flex w-full min-h-0 ${rpModalRowLayout ? 'min-h-0 flex-row' : 'flex-col'}`}>
          {/* Strip Preview */}
          <div
            className={`flex items-center justify-center bg-gray-50/50 ${rpModalRowLayout ? 'overflow-hidden' : 'overflow-visible'} ${stripPaneClass}`}
          >
            {stripPreview ? (
              <div className="flex w-full min-w-0 items-center justify-center">
                {stripPreviewIsVideo ? (
                  <video
                    src={stripPreview}
                    autoPlay
                    loop
                    muted
                    playsInline
                    className={`h-auto w-auto object-contain object-center drop-shadow-2xl rounded-lg ${stripMediaMax}`}
                  />
                ) : (
                  <img
                    src={stripPreview}
                    alt="Photo Strip"
                    className={`h-auto w-auto object-contain object-center drop-shadow-2xl ${stripMediaMax}`}
                  />
                )}
              </div>
            ) : (
              <div className="flex h-48 w-full items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
              </div>
            )}
          </div>

          {/* Content */}
          <div
            className={`custom-scrollbar flex min-h-0 min-w-0 flex-col overflow-y-auto overflow-x-hidden bg-white/40 ${rpModalRowLayout ? 'flex-1' : 'w-full shrink-0'} ${rpContentJustify} ${contentPadClass}`}
          >
            {showPayment ? (
              <div className={`flex animate-fade-in flex-col items-center justify-center ${rpTiny ? 'py-2' : 'py-4'}`}>
                <h2 className={`text-gray-800 ${paymentTitleClass}`}>
                  {t('reprint.scanToPay', 'Complete Payment')}
                </h2>

                <div className={paymentQrBoxClass}>
                  {isExpired ? (
                    <div className="flex flex-col items-center text-red-500">
                      <p className="font-bold mb-2">Payment Expired</p>
                      <button onClick={handlePayment} className="text-sm text-blue-600 underline">
                        Try Again
                      </button>
                    </div>
                  ) : qrUrl ? (
                    <QrCodeImage
                      qrUrl={qrUrl}
                      className="w-full h-full object-contain"
                      onEvent={qrActivityLogger(logActivity, 'reprint')}
                    />
                  ) : snapToken ? (
                    <div className={`flex flex-col items-center ${rpTiny ? 'gap-2' : 'gap-4'}`}>
                      <p className={`text-center text-gray-600 ${rpTiny ? 'text-xs' : rpCompact ? 'text-sm' : 'text-base'}`}>
                        Secure Payment Popup Ready
                      </p>
                      <Button
                        text="Pay Now"
                        onClick={openSnapPayment}
                        variant="primary"
                        className={reprintPrimaryBtn}
                      />
                    </div>
                  ) : redirectUrl ? (
                    // Redirect rail: the frameless gateway window opens over the
                    // kiosk automatically. Show a spinner underneath, or surface
                    // a retry if the popup failed to open.
                    billWindowError ? (
                      <div className={`flex flex-col items-center text-center ${rpTiny ? 'gap-2' : 'gap-4'}`}>
                        <p className={`text-red-500 ${rpTiny ? 'text-xs' : 'text-sm'}`}>
                          {billWindowError}
                        </p>
                        <Button
                          text={t('payment.popupRetry', 'Retry')}
                          onClick={() => openBillWindow()}
                          disabled={billWindowOpen}
                          variant="primary"
                          className={reprintPrimaryBtn}
                        />
                      </div>
                    ) : (
                      <div className="flex items-center justify-center">
                        <div
                          className={`animate-spin rounded-full border-b-2 border-blue-600 ${rpTiny ? 'h-8 w-8' : 'h-12 w-12'}`}
                        />
                      </div>
                    )
                  ) : (
                    <div className="flex items-center justify-center">
                      <div
                        className={`animate-spin rounded-full border-b-2 border-blue-600 ${rpTiny ? 'h-8 w-8' : 'h-12 w-12'}`}
                      />
                    </div>
                  )}
                </div>

                <p className={`mb-2 text-gray-800 ${paymentPriceClass}`}>
                  {formatAmount(currentPrice, projectCurrency)}
                </p>
                <p
                  className={`max-w-sm text-center text-gray-600 ${rpTiny ? 'mb-3 text-xs' : rpCompact ? 'mb-4 text-sm' : isSpacious ? 'mb-8 text-lg' : 'mb-8 text-base'}`}
                >
                  {qrUrl
                    ? t('reprint.paymentInstruction', 'Scan the QR code to complete purchase')
                    : redirectUrl
                      ? t(
                          'reprint.redirectInstruction',
                          'Complete payment in the window that just opened.'
                        )
                      : 'Click Pay Now to complete your purchase securely.'}
                </p>

                <Button
                  onClick={handleCancelPayment}
                  text={t('reprint.cancel', 'Cancel')}
                  variant="secondary"
                  className={`max-w-sm ${reprintSecondaryBtn}`}
                />
              </div>
            ) : (
              <>
                <div className={`text-center ${sectionGap}`}>
                  <h1 className={reprintTitleClass}>{t('reprint.wantExtras', 'Want Extra Prints?')}</h1>
                  <p className={reprintSubtitleClass}>
                    {t('reprint.subtitle', 'Get more copies of your photo strip')}
                  </p>
                </div>

                <div className={`flex flex-col items-center ${sectionGap}`}>
                  <div className={`mb-3 flex items-center ${stepperOuterGap}`}>
                    <button
                      title="Decrease Quantity"
                      type="button"
                      onClick={() => handleQuantityChange('decrease')}
                      className={stepperBtnClass}
                      disabled={isMinQuantity}
                    >
                      <FaMinus className={stepperIconClass} />
                    </button>

                    <div className={`flex flex-col items-center ${qtyColMin}`}>
                      <span className={`font-bold tabular-nums text-gray-800 ${qtyNumClass}`}>
                        {selectedQuantity}
                      </span>
                      <span className={qtyLabelClass}>{t('reprint.quantity', 'Copies')}</span>
                    </div>

                    <button
                      title="Increase Quantity"
                      type="button"
                      onClick={() => handleQuantityChange('increase')}
                      className={stepperBtnClass}
                      disabled={isMaxQuantity}
                    >
                      <FaPlus className={stepperIconClass} />
                    </button>
                  </div>

                  <div className="mt-1 flex flex-col items-center">
                    <p className={`text-blue-600 ${priceMainClass}`}>
                      {currentPrice > 0
                        ? formatAmount(currentPrice, projectCurrency)
                        : t('reprint.free', 'Free')}
                    </p>
                    {frameAddon > 0 && packagePrice > 0 && (
                      <p className={`mt-0.5 text-gray-500 ${rpTiny ? 'text-[10px]' : rpCompact ? 'text-xs' : 'text-sm'}`}>
                        {t('frameSurcharge.lineItem', 'Frame add-on')}{' '}
                        {t('frameSurcharge.badge', '+{{price}}', {
                          price: formatAmount(frameAddon, projectCurrency)
                        })}
                      </p>
                    )}
                  </div>
                </div>

                {/*
                  No voucher-only banner here either — the relabelled
                  "Use Voucher Code" CTA below already invites the customer
                  action. Owner-facing messaging belongs in the dashboard.
                */}
                <div className={`flex flex-col ${buttonGap}`}>
                  <Button
                    text={
                      currentPrice <= 0
                        ? t('reprint.printFree', 'Print')
                        : isVoucherOnlyRegion
                          ? t('reprint.useVoucher', 'Use Voucher Code')
                          : t('reprint.print', 'Pay & Print')
                    }
                    onClick={handlePayment}
                    disabled={
                      (stripPreviewIsVideo ? !stripUrlForPrint : !stripPreview) ||
                      isProcessing ||
                      !selectedPackage
                    }
                    variant="primary"
                    className={reprintPrimaryBtn}
                  />
                  <Button
                    text={t('reprint.skip', 'Skip')}
                    onClick={handleSkip}
                    variant="secondary"
                    className={reprintSecondaryBtn}
                  />
                </div>
              </>
            )}
          </div>
        </div>
      </GlassPanel>

      {/*
        Operator-bypass: numeric 6-digit PIN matched against
        `settingsStore.password`. Reserved for the offline / Control Panel
        override path — explicitly NOT used for the customer voucher-only
        flow below (operators must never share their PIN with customers).
      */}
      <PinModal
        isOpen={showPinModal}
        onClose={() => setShowPinModal(false)}
        onSubmit={handlePinSubmit}
        title={t('payment.codeEntry.title', 'Enter Access Code')}
        subtitle={t(
          'payment.codeEntry.subtitle',
          'Enter 6-digit code to bypass payment'
        )}
      />

      {/*
        Customer voucher entry: alphanumeric code validated against
        `/api/promos/apply`. Used when the project is voucher-only (no QRIS
        rail, no custom gateway) or when the backend declines mid-flow.
        Same UI contract as view/Checkout's promo input.
      */}
      {showVoucherInput && (
        <OnScreenKeyboard
          onEnter={handleVoucherSubmit}
          placeholder={t(
            'checkout.voucherCodePlaceholder',
            'Type or paste your code'
          )}
          error={voucherError}
          isLoading={isVoucherLoading}
          onInputChange={() => setVoucherError('')}
          onClose={() => {
            setShowVoucherInput(false)
            setVoucherError('')
          }}
        />
      )}
    </BackgroundContainer>
  )
}

export default UserReprintModal
